/**
 * Toast — Lightweight notification with auto-dismiss
 */
import { useState, useEffect, useCallback } from 'preact/hooks'
import type { JSX } from 'preact'

export interface ToastMessage {
  id: string
  type: 'success' | 'error' | 'info'
  message: string
}

interface ToastProps {
  toasts: ToastMessage[]
  onDismiss: (id: string) => void
}

export function Toast({ toasts, onDismiss }: ToastProps): JSX.Element | null {
  if (toasts.length === 0) return null

  return (
    <div className="bw2-toast-container" aria-live="polite">
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  )
}

function ToastItem({ toast, onDismiss }: { toast: ToastMessage; onDismiss: (id: string) => void }): JSX.Element {
  const [isExiting, setIsExiting] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsExiting(true)
      setTimeout(() => onDismiss(toast.id), 200)
    }, 3000)
    return () => clearTimeout(timer)
  }, [toast.id, onDismiss])

  const handleDismiss = useCallback(() => {
    setIsExiting(true)
    setTimeout(() => onDismiss(toast.id), 200)
  }, [toast.id, onDismiss])

  return (
    <div
      className={`bw2-toast bw2-toast--${toast.type}${isExiting ? ' bw2-toast--exiting' : ''}`}
      role="alert"
    >
      <span className="bw2-toast__icon">
        {toast.type === 'success' ? '✓' : toast.type === 'error' ? '✕' : 'ℹ'}
      </span>
      <span className="bw2-toast__message">{toast.message}</span>
      <button
        className="bw2-toast__close"
        onClick={handleDismiss}
        type="button"
        aria-label="Dismiss"
      >
        ✕
      </button>
    </div>
  )
}
